import { TileData, Rotation, TileType } from '../types';
import { findTileVariant } from '../constants/tiles';
import type { Port } from '../types';

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

type Cell = [number, number];

const DIRS: Array<{ port: Port; dr: number; dc: number }> = [
  { port: 'N', dr: -1, dc: 0 },
  { port: 'E', dr: 0,  dc: 1 },
  { port: 'S', dr: 1,  dc: 0 },
  { port: 'W', dr: 0,  dc: -1 },
];

/**
 * Find a single winding path from (0,0) to (endR,endC).
 * Runs multiple DFS attempts and returns the longest path found.
 */
function findLongestPath(gridSize: number, endR: number, endC: number): Cell[] {
  let bestPath: Cell[] = [];

  for (let attempt = 0; attempt < 60; attempt++) {
    // Alternate between "explore away from end" bias and pure random
    const biasAway = attempt % 3 !== 0;

    const visited = new Set<string>(['0,0']);
    const path: Cell[] = [[0, 0]];

    const dfs = (r: number, c: number): void => {
      if (r === endR && c === endC) {
        if (path.length > bestPath.length) {
          bestPath = [...path];
        }
        return;
      }

      let dirs = shuffle([...DIRS]);

      if (biasAway) {
        // Prefer going AWAY from end first — forces the path to wind through the grid
        dirs = [...dirs].sort((a, b) => {
          const da = Math.abs((r + a.dr) - endR) + Math.abs((c + a.dc) - endC);
          const db = Math.abs((r + b.dr) - endR) + Math.abs((c + b.dc) - endC);
          return db - da;
        });
      }

      for (const { dr, dc } of dirs) {
        const nr = r + dr;
        const nc = c + dc;
        const key = `${nr},${nc}`;
        if (nr >= 0 && nr < gridSize && nc >= 0 && nc < gridSize && !visited.has(key)) {
          visited.add(key);
          path.push([nr, nc]);
          dfs(nr, nc);
          path.pop();
          visited.delete(key);
        }
      }
    };

    dfs(0, 0);

    // Early exit if near-Hamiltonian path found
    if (bestPath.length >= gridSize * gridSize - 2) break;
  }

  // Fallback: guaranteed simple path if nothing found
  if (bestPath.length === 0) {
    const visited = new Set<string>(['0,0']);
    const path: Cell[] = [[0, 0]];

    const simpleDfs = (r: number, c: number): boolean => {
      if (r === endR && c === endC) return true;
      for (const { dr, dc } of shuffle([...DIRS])) {
        const nr = r + dr;
        const nc = c + dc;
        const key = `${nr},${nc}`;
        if (nr >= 0 && nr < gridSize && nc >= 0 && nc < gridSize && !visited.has(key)) {
          visited.add(key);
          path.push([nr, nc]);
          if (simpleDfs(nr, nc)) return true;
          path.pop();
          visited.delete(key);
        }
      }
      return false;
    };

    simpleDfs(0, 0);
    bestPath = [...path];
  }

  return bestPath;
}

/**
 * Get a scramble rotation that guarantees a DIFFERENT logical state.
 * Straight tiles only have 2 distinct states (N/S vs E/W), so must use 90°/270°.
 */
function getSafeScramble(type: TileType): Rotation {
  if (type === 'straight') {
    return Math.random() < 0.5 ? 90 : 270;
  }
  const opts: Rotation[] = [90, 180, 270];
  return opts[Math.floor(Math.random() * 3)];
}

export function generatePuzzle(gridSize: number): TileData[][] {
  const endR = gridSize - 1;
  const endC = gridSize - 1;

  // Step 1: Find longest winding single path from (0,0) to (endR, endC)
  const path = findLongestPath(gridSize, endR, endC);
  const pathSet = new Set(path.map(([r, c]) => `${r},${c}`));

  // Step 2: Build port sets for each path cell from entry/exit directions
  const portSets = new Map<string, Set<Port>>();
  for (let i = 0; i < path.length; i++) {
    const [r, c] = path[i];
    const ports = new Set<Port>();

    if (i > 0) {
      const [pr, pc] = path[i - 1];
      const dr = pr - r, dc = pc - c;
      if (dr === -1) ports.add('N');
      else if (dr === 1) ports.add('S');
      else if (dc === -1) ports.add('W');
      else if (dc === 1) ports.add('E');
    }
    if (i < path.length - 1) {
      const [nr, nc] = path[i + 1];
      const dr = nr - r, dc = nc - c;
      if (dr === -1) ports.add('N');
      else if (dr === 1) ports.add('S');
      else if (dc === -1) ports.add('W');
      else if (dc === 1) ports.add('E');
    }

    portSets.set(`${r},${c}`, ports);
  }

  // Step 3: Build the full grid
  const solvedGrid: TileData[][] = Array.from({ length: gridSize }, (_, r) =>
    Array.from({ length: gridSize }, (__, c) => {
      const key = `${r},${c}`;

      if (!pathSet.has(key)) {
        return { type: 'empty' as TileType, logicalRotation: 0 as Rotation, displayRotation: 0, isOnPath: false };
      }

      const ports = portSets.get(key)!;
      const variant = findTileVariant(ports);

      return {
        type: variant ? variant.type : ('deadend' as TileType),
        logicalRotation: variant ? variant.rotation : (0 as Rotation),
        displayRotation: variant ? variant.rotation : 0,
        isOnPath: true,
      };
    })
  );

  // Step 4: Scramble path tiles — source (0,0) and end (endR,endC) are locked
  for (let r = 0; r < gridSize; r++) {
    for (let c = 0; c < gridSize; c++) {
      const tile = solvedGrid[r][c];
      if (!tile.isOnPath) continue;
      if (r === 0 && c === 0) continue;       // source locked
      if (r === endR && c === endC) continue; // end locked

      const scramble = getSafeScramble(tile.type);
      const newLogical = (((tile.logicalRotation / 90 + scramble / 90) % 4) * 90) as Rotation;
      solvedGrid[r][c] = { ...tile, logicalRotation: newLogical, displayRotation: newLogical };
    }
  }

  return solvedGrid;
}
